/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import { useField } from 'react-final-form';

import { FileInput as Input, FileInputValue } from '@vk-hr-tek/ui/input';

import { composeValidators, ValidateType } from '../validation';

/**
 * Интерфейс пропсов компонента FileInput.
 *
 * @typedef {Object} FileInputProps
 * @property {string} label - Подпись поля.
 * @property {React.ReactNode} [tooltip] - Всплывающая подсказка.
 * @property {string} name - Имя поля.
 * @property {'input' | 'preview'} [variant] - Вариант отображения поля.
 * @property {string} [placeholder] - Текст заполнителя.
 * @property {boolean} [required] - Обязательность поля.
 * @property {boolean} [disabled] - Недоступность поля.
 * @property {(value?: FileInputValue) => void} [onChange] - Обработчик изменения значения.
 * @property {boolean} [loading] - Состояние загрузки.
 * @property {boolean} [showErrorWithoutTouched] - Показывать ошибку без взаимодействия с полем.
 * @property {ValidateType[]} [validate] - Массив валидаторов.
 * @property {(file: File) => Promise<{ file_id: string; expired_at: string }>} [cache] - Функция для кэширования файла.
 * @property {boolean} [fullWidth] - Занимать всю ширину контейнера.
 * @property {string} [bannerText] - Текст баннера.
 * @property {string} [helperText] - Дополнительный текст помощи.
 * @property {string} [testId] - Идентификатор для тестирования.
 * @property {boolean} [validationWithoutTouch=false] - Показывать ошибку без взаимодействия с полем.
 * @property {boolean} [validationWhenDisabled=false] - Показывать ошибку если поле задизейблено.
 */

type FileInputProps = {
  label: string;
  tooltip?: React.ReactNode;
  name: string;
  variant?: 'input' | 'preview';
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  onChange?: (value?: FileInputValue) => void;
  loading?: boolean;
  loadingText?: JSX.Element;
  showErrorWithoutTouched?: boolean;
  validate?: ValidateType[];
  cache?: (file: File) => Promise<{ file_id: string; expired_at: string }>;
  fullWidth?: boolean;
  bannerText?: string;
  autorecognitionQr?: boolean;
  helperText?: string;
  testId?: string;
  withZoom?: boolean;
  withAuth?: boolean;
  validationWithoutTouch?: boolean;
  validationWhenDisabled?: boolean;
};

/**
 * Компонент для загрузки файла с возможностью предварительного просмотра.
 *
 * @param {FileInputProps} props - Свойства компонента.
 * @param {string} props.label - Подпись поля.
 * @param {React.ReactNode} [props.tooltip] - Всплывающая подсказка.
 * @param {string} props.name - Имя поля.
 * @param {'input' | 'preview'} [props.variant='input'] - Вариант отображения поля.
 * @param {string} [props.placeholder] - Текст заполнителя.
 * @param {boolean} [props.required=false] - Обязательность поля.
 * @param {boolean} [props.disabled=false] - Недоступность поля.
 * @param {(value?: FileInputValue) => void} [props.onChange] - Обработчик изменения значения.
 * @param {boolean} [props.loading=false] - Состояние загрузки.
 * @param {boolean} [props.withZoom=false] - Флаг для масштабирования документа.
 * @param {ValidateType[]} [props.validate] - Массив валидаторов.
 * @param {(file: File) => Promise<{ file_id: string; expired_at: string }>} [props.cache] - Функция для кэширования файла.
 * @param {boolean} [props.fullWidth] - Занимать всю ширину контейнера.
 * @param {string} [props.bannerText] - Текст баннера.
 * @param {string} [props.helperText] - Дополнительный текст помощи.
 * @param {string} [props.testId] - Идентификатор для тестирования.
 * @param {boolean} [props.withAuth] - Флаг авторизации.
 * @param {boolean} [props.validationWithoutTouch=false] - Показывать ошибку без взаимодействия с полем.
 * @param {boolean} [props.validationWhenDisabled=false] - Показывать ошибку если поле задизейблено.
 * @returns {React.ReactElement} - Элемент React.
 */
export const FileInput = ({
  label,
  tooltip,
  name,
  variant = 'input',
  placeholder,
  required = false,
  disabled = false,
  onChange,
  loading = false,
  loadingText = undefined,
  validate,
  cache,
  fullWidth,
  bannerText,
  autorecognitionQr = false,
  helperText,
  testId,
  withAuth,
  withZoom = false,
  validationWithoutTouch = false,
  validationWhenDisabled = false,
}: FileInputProps) => {
  const validators = composeValidators(validate || []);
  const { input, meta } = useField(name, {
    validate: validators,
  });

  const { onChange: onChangeInput, onBlur } = input;

  /**
   * Флаг для отображения ошибки в зависимости от состояния disabled
   * @type {boolean}
   */
  const errorDisabled = disabled || !validationWhenDisabled;

  /**
   * Флаг для отображения ошибки
   * @type {boolean}
   */
  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && meta.error;

  return (
    <Input
      {...input}
      label={label}
      value={input.value}
      onChange={(props) => {
        onChangeInput(props);
        onChange?.(props);
      }}
      variant={variant}
      tooltip={tooltip}
      required={required}
      error={renderError}
      errorText={renderError && meta.error}
      helperText={helperText}
      disabled={disabled}
      loading={loading}
      loadingText={loadingText}
      onBlur={onBlur}
      cache={cache}
      fullWidth={fullWidth}
      placeholder={placeholder}
      autorecognitionQr={autorecognitionQr}
      bannerText={bannerText}
      testId={testId}
      withZoom={withZoom}
      validators={validators}
      withAuth={withAuth}
    />
  );
};
